import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { clearStoredUser, getStoredUser, onStoredUserChange } from "../lib/auth";
import { useLanguage } from "../lib/language";
import api from "../lib/api";
import { getProfileImageStyle } from "../lib/profileImage";
import { getPreferredDisplayName, repairMojibakeDeep } from "../lib/text";
import BrandLogo from "./BrandLogo";

function AppShell() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredUser());
  const { language, toggleLanguage, t } = useLanguage();
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordMessageType, setPasswordMessageType] = useState("info");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    return onStoredUserChange((event) => {
      setUser(event.detail || null);
    });
  }, []);

  const shellCopy = useMemo(() => repairMojibakeDeep(
    language === "ar"
      ? {
          workspace: "مساحة العمل",
          account: "الحساب",
          trainerOverview: "لوحة المتابعة",
          trainerAnalytics: "التحليلات",
          trainerCourses: "الكورسات",
          trainerResults: "تفاصيل النتائج",
          profile: "الملف الشخصي",
          welcome: "تجربة أكثر ترتيبًا للمدرب والطالب",
          manageAccount: "إدارة الحساب",
          logoutNow: "تسجيل الخروج",
          adminUnit: "وحدة الإدارة",
          adminOverview: "نظرة عامة",
          trainerManagement: "إدارة المدربين",
          studentManagement: "إدارة الطلبة",
          courseManagement: "إدارة الكورسات"
        }
      : {
          workspace: "Workspace",
          account: "Account",
          trainerOverview: "Overview",
          trainerAnalytics: "Analytics",
          trainerCourses: "Courses",
          trainerResults: "Detailed Results",
          profile: "Profile",
          welcome: "A cleaner trainer and student experience",
          manageAccount: "Manage account",
          logoutNow: "Log out",
          adminUnit: "Admin Unit",
          adminOverview: "Overview",
          trainerManagement: "Trainer Management",
          studentManagement: "Student Management",
          courseManagement: "Course Management"
        }
  ), [language]);

  const navigationGroups = useMemo(() => {
    if (user?.role === "admin") {
      return [
        {
          title: shellCopy.adminUnit,
          items: [
            { label: shellCopy.adminOverview, to: "/admin" },
            { label: shellCopy.trainerManagement, to: "/admin/trainers" },
            { label: shellCopy.studentManagement, to: "/admin/students" },
            { label: shellCopy.courseManagement, to: "/admin/courses" }
          ]
        },
        {
          title: shellCopy.account,
          items: [{ label: shellCopy.profile, to: "/profile" }]
        }
      ];
    }

    if (user?.role === "trainer") {
      return [
        {
          title: shellCopy.workspace,
          items: [
            { label: t("trainerDashboard"), to: "/dashboard" },
            { label: shellCopy.trainerOverview, to: "/trainer-overview" },
            { label: shellCopy.trainerAnalytics, to: "/trainer-analytics" },
            { label: shellCopy.trainerResults, to: "/trainer-results" },
            { label: shellCopy.trainerCourses, to: "/courses" },
            { label: t("aiStudio"), to: "/ai-studio" }
          ]
        },
        {
          title: shellCopy.account,
          items: [{ label: shellCopy.profile, to: "/profile" }]
        }
      ];
    }

    return [
      {
        title: shellCopy.workspace,
        items: [{ label: t("studentExams"), to: "/exam" }]
      },
      {
        title: shellCopy.account,
        items: [{ label: shellCopy.profile, to: "/profile" }]
      }
    ];
  }, [shellCopy, t, user?.role]);

  const handleLogout = () => {
    clearStoredUser();
    navigate("/", { replace: true });
  };

  const displayName = getPreferredDisplayName(user, language === "ar" ? "المستخدم" : "User");

  const avatarLabel = displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

  const passwordText = repairMojibakeDeep(language === "ar"
    ? {
        button: "تغيير كلمة المرور",
        title: "تغيير كلمة المرور",
        current: "كلمة المرور الحالية",
        next: "كلمة المرور الجديدة",
        confirm: "تأكيد كلمة المرور الجديدة",
        save: "حفظ كلمة المرور",
        saving: "جارٍ الحفظ...",
        cancel: "إلغاء",
        mismatch: "كلمتا المرور الجديدتان غير متطابقتين",
        tooShort: "كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل",
        success: "تم تحديث كلمة المرور بنجاح"
      }
    : {
        button: "Change Password",
        title: "Change Password",
        current: "Current Password",
        next: "New Password",
        confirm: "Confirm New Password",
        save: "Save Password",
        saving: "Saving...",
        cancel: "Cancel",
        mismatch: "The new passwords do not match",
        tooShort: "The new password must be at least 6 characters long",
        success: "Password updated successfully"
      });

  const resetPasswordModal = () => {
    setPasswordForm({
      current_password: "",
      new_password: "",
      confirm_password: ""
    });
    setPasswordMessage("");
    setPasswordMessageType("info");
    setIsPasswordModalOpen(false);
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password.length < 6) {
      setPasswordMessageType("error");
      setPasswordMessage(passwordText.tooShort);
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordMessageType("error");
      setPasswordMessage(passwordText.mismatch);
      return;
    }

    try {
      setIsSavingPassword(true);
      await api.post("/account/change-password", {
        user_id: user?.id,
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setPasswordMessageType("success");
      setPasswordMessage(passwordText.success);
      window.setTimeout(() => {
        resetPasswordModal();
      }, 900);
    } catch (error) {
      setPasswordMessageType("error");
      setPasswordMessage(error.response?.data?.error || t("serverError"));
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-brand">
          <BrandLogo compact />
          <p className="topbar-subtitle">{shellCopy.welcome}</p>
        </div>

        <div className="topbar-actions">
          <button className="language-toggle" onClick={toggleLanguage} type="button">
            {language === "ar" ? t("switchToEnglish") : t("switchToArabic")}
          </button>

          <div className="user-badge">
            {user?.profile_image_url ? (
              <img src={user.profile_image_url} alt={displayName} className="shell-avatar" style={getProfileImageStyle(user)} />
            ) : (
              <div className="shell-avatar shell-avatar--fallback">{avatarLabel}</div>
            )}
            <div className="user-badge__content">
              <span className="user-badge__name">{displayName}</span>
              <span className="user-badge__role">
                {user?.role === "admin"
                  ? t("adminRole")
                  : user?.role === "trainer"
                    ? t("trainerRole")
                    : t("studentRole")}
              </span>
            </div>
          </div>

          <button className="ghost-button" onClick={() => setIsPasswordModalOpen(true)} type="button">
            {passwordText.button}
          </button>

          <button className="secondary-button" onClick={handleLogout}>
            {t("logout")}
          </button>
        </div>
      </header>

      <div className="app-layout">
        <aside className="sidebar">
          <div className="sidebar-card">
            <p className="sidebar-card__eyebrow">{shellCopy.manageAccount}</p>
            <div className="sidebar-profile">
              {user?.profile_image_url ? (
                <img src={user.profile_image_url} alt={displayName} className="sidebar-avatar" style={getProfileImageStyle(user)} />
              ) : (
                <div className="sidebar-avatar sidebar-avatar--fallback">{avatarLabel}</div>
              )}
              <div className="stack-sm">
                <strong>{displayName}</strong>
                <span className="muted-text">
                  {user?.role === "admin"
                    ? t("adminRole")
                    : user?.role === "trainer"
                      ? t("trainerRole")
                      : t("studentRole")}
                </span>
              </div>
            </div>
          </div>

          {navigationGroups.map((group) => (
            <nav key={group.title} className="nav-group">
              <p className="nav-group__title">{group.title}</p>
              <div className="nav-list">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => (isActive ? "nav-link nav-link--active" : "nav-link")}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </nav>
          ))}

          <button className="secondary-button sidebar-logout" onClick={handleLogout} type="button">
            {shellCopy.logoutNow}
          </button>
        </aside>

        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {isPasswordModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{passwordText.title}</h3>
            <div className="stack-lg">
              <input
                className="text-input"
                type="password"
                placeholder={passwordText.current}
                value={passwordForm.current_password}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, current_password: event.target.value }))
                }
              />
              <input
                className="text-input"
                type="password"
                placeholder={passwordText.next}
                value={passwordForm.new_password}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, new_password: event.target.value }))
                }
              />
              <input
                className="text-input"
                type="password"
                placeholder={passwordText.confirm}
                value={passwordForm.confirm_password}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, confirm_password: event.target.value }))
                }
              />
              {passwordMessage && (
                <div className={`feedback-banner feedback-banner--${passwordMessageType}`}>{passwordMessage}</div>
              )}
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={resetPasswordModal} type="button">
                {passwordText.cancel}
              </button>
              <button className="primary-button" onClick={handlePasswordChange} type="button" disabled={isSavingPassword}>
                {isSavingPassword ? passwordText.saving : passwordText.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppShell;
